// 3rd Party
import moment from 'moment';

// Constants
import { ASSET_GROUPS, DEFAULT_GROWTH_VALUES, Interval } from '../constants';
import { CURRENCY } from '../currency';
import { LOCALE, formatMessage } from '../i18n';

// Utils
import { daysBetween, monthsDiff, yearsDiff } from '../time';
import { HOLDING_TYPES, getPortfolioType } from '../portfolios';
import { DISCLAIMER_KEYS } from '../disclaimers/constants';
import disclaimers from '../disclaimers';
import { generateBSONId, generateTempId } from '../random';
import {
  calculateMonthlyRollings,
  calculateWeeklyRollings,
  calculateWeeklyNAV,
  calculateMonthlyNAV,
  calculatePercentGrowthFromPrices,
  getValidPeriods,
  formatRollings,
  getVarianceMetrics,
  calculateMaximumDrawdown,
  calculateBestWorstPerformance,
  calculateUpDownMonths,
  calculateCalendarYearFromRolling,
  calculatePeriodicalRate,
  initRiskData,
} from '../calculation-utils';

import { generateItemCopyName } from '../comparison';
import { deepClone } from '../objects';
import Dictionary from '@/types/dictionary';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import ComparisonHolding from '@/types/comparison-holding';
import Nullable from '@/types/nullable';
import ComparisonHoldingType from '@/types/comparison-holding-type';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import Portfolio from '@/types/portfolio';
import ComparisonItem from '@/types/comparison-item';
import CustomYieldParams from '@/types/custom-yield-params';
import Timestamp from '@/types/timestamp';
import FundDates from '@/types/fund-dates';
import PriceData from '@/types/price-data';
import ShelvableInstrument from '@/types/shelvable-instrument';
import CompliantInstrument from '@/types/compliant-instrument';
import Fund from '@/types/fund';
import Allocations from '@/types/allocations';
import RollingPerformance from '@/types/rolling-performance';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import CommonInception from '@/types/common-inception';
import InstrumentPerformance from '@/types/instruments/instrument-performance';

/*
  Rebalancing periods used for rolling performance calculations.
  Values should be interpreted as: If I rebalance annually based on weekly periods, I will rebalance every 52 periods.
*/

export type RebalanceFrequency = {
  value: string;
  monthly: number;
  weekly: number;
};

export type CalculatePortfolioOptions = {
  showLongerHistory?: boolean;
  taxRate?: number;
  reallocate?: boolean;
  commonInception?: CommonInception;
};

export const REBALANCING_PERIODS: Dictionary<RebalanceFrequency> = {
  YEARLY: {
    value: 'yearly',
    weekly: 52,
    monthly: 12,
  },
  SEMIANNUALLY: {
    value: 'semiAnnually',
    weekly: 26,
    monthly: 6,
  },
  QUARTERLY: {
    value: 'quarterly',
    weekly: 13,
    monthly: 3,
  },
  DAILY: {
    value: 'daily',
    weekly: 1,
    monthly: 1,
  },
};

/**
 * Fetch the total cost value for a Portfolio or Comparison Item
 *
 * @param {Portfolio | ComparisonItem} item Portfolio or Comparison Item.
 * @return Value of MER and Advisory Fee
 */
export const getTotalMer = (item: Portfolio | ComparisonItem) => {
  if (item.mer == null && item.discretionaryMer == null) {
    return null;
  }

  return (item.mer || 0) + (item.discretionaryMer || 0);
};

/**
 * Check that **every** portfolio holding has monthly data
 * @param data the holdings of a portfolio
 * @returns {boolean}
 */
export const portfolioHasMonthlyData = (holdings: Holding[]): boolean =>
  holdings.every(item => item.performance && item.performance.rolling && item.performance.rolling.monthly && item.performance.rolling.monthly.length > 0);

/**
 * Gets the holding with the latest **monthly** timestamp
 * @param holdings the holdings of a portfolio
 */
export const getHoldingWithEarliestEnding = (holdings: Holding[]): Holding =>
  holdings.reduce((prev, curr) => {
    const prevLast = prev.performance.rolling.monthly.length - 1;
    const currLast = curr.performance.rolling.monthly.length - 1;
    return prev.performance.rolling.monthly[prevLast][0] <= curr.performance.rolling.monthly[currLast][0] ? prev : curr;
  });

export const rollingPerformance = (
  holdings: { id: string; rolling?: RollingPerformance; weight: number }[],
  interval: Interval,
  fees: number = 0,
  rebalanceFreq?: RebalanceFrequency,
  showLongerHistory: boolean = false,
): [number, number][] => {
  if (!holdings.every(h => !!h.rolling && !!h.rolling[interval])) {
    return [];
  }

  type ChangeDataPoint = {
    change: number;
    weight: number;
    id: string;
  };

  type ChangeDataPointTuple = {
    key: number;
    data: ChangeDataPoint[];
  };

  // combine data into object
  const rawTimeValues = holdings.reduce(
    (acc: Dictionary<ChangeDataPoint[]>, holding) => {
      if (holding.rolling) {
        const effectiveFee = calculatePeriodicalRate(fees, interval === Interval.Weekly ? 52 : 12);
        holding.rolling[interval].forEach((data: [number, number]) => {
          const val: ChangeDataPoint = { change: (data[1] - effectiveFee) / 100, weight: holding.weight / 100, id: holding.id };
          const key = data[0];

          if (!acc[key]) {
            acc[key] = [val];
          } else {
            acc[key].push(val);
          }
        });
      }

      return acc;
    },
    {} as Dictionary<ChangeDataPoint[]>,
  );

  let timeValues = rawTimeValues;
  if (!showLongerHistory) {
    timeValues = Object.keys(rawTimeValues).reduce(
      (acc, key) => {
        if (rawTimeValues[key].length === holdings.length) {
          acc[key] = rawTimeValues[key];
        }
        return acc;
      },
      {} as Dictionary<ChangeDataPoint[]>,
    );
  }

  // Normalize data for any missing data points plus re-weight for short history
  for (const item of Object.values<ChangeDataPoint[]>(timeValues)) {
    if (item.length < holdings.length) {
      const existing = item.map((i: ChangeDataPoint) => i.id);
      const missing = holdings.filter(holding => !existing.some((ex: string) => ex === holding.id));
      if (showLongerHistory && missing.length) {
        const weights =
          missing.reduce((acc, curr) => {
            acc += curr.weight;
            return acc;
          }, 0) / 100;
        item.forEach(changePoint => {
          changePoint.weight /= 1 - weights;
        });
      }

      missing.forEach(m => {
        item.push({
          change: 0,
          weight: showLongerHistory ? 0 : m.weight / 100,
          id: m.id,
        });
      });
    }
    item.sort((a: ChangeDataPoint, b: ChangeDataPoint) => a.id.localeCompare(b.id));
  }

  // Transform object into array
  const sortedTimeValues: ChangeDataPointTuple[] = Object.keys(timeValues).map((key: string) => ({ key: Number(key), data: timeValues[key] }));

  // sort array
  sortedTimeValues.sort((a, b) => a.key - b.key);

  const rebalancePeriod = rebalanceFreq ? rebalanceFreq[interval] : 1;

  let previousPeriod: any = null;

  // These functions are declared outside of the scope to prevent closure around the 'for' loop
  const getGrossFromPrevious = (p: ChangeDataPoint, i: number) => (previousPeriod.grossWeight[i] / previousPeriod.grossWeightSum) * (1 + p.change);
  const getRollingFromPrevious = (p: ChangeDataPoint, i: number) => p.change * (previousPeriod.grossWeight[i] / previousPeriod.grossWeightSum);

  sortedTimeValues.forEach((val: any, i) => {
    // Either the first item or when portfolio gets rebalanced
    if (i === 0 || i % rebalancePeriod === 0) {
      // Use default weight values
      val.grossWeight = val.data.map((p: ChangeDataPoint) => p.weight * (1 + p.change));
      val.rollingPerformance = val.data.map((p: ChangeDataPoint) => p.change * p.weight);
    } else {
      val.grossWeight = val.data.map(getGrossFromPrevious);
      val.rollingPerformance = val.data.map(getRollingFromPrevious);
    }

    val.grossWeightSum = val.grossWeight.reduce((acc: number, gw: number) => acc + gw);
    val.rollingPerformanceSum = val.rollingPerformance.reduce((acc: number, rp: number) => acc + rp);

    // Store previous period data
    previousPeriod = val;
  });

  const results: [number, number][] = [];
  for (let i = 0; i < sortedTimeValues.length; i++) {
    results.push([sortedTimeValues[i].key, (sortedTimeValues[i] as any).rollingPerformanceSum * 100]);
  }

  return results;
};

export const calculateGrowth = (holdings: ComparisonHolding[], fees?: number, rebalanceFreq?: RebalanceFrequency, showLongerHistory: boolean = false) => ({
  weekly: rollingPerformance(holdings, Interval.Weekly, fees, rebalanceFreq, showLongerHistory),
  monthly: rollingPerformance(holdings, Interval.Monthly, fees, rebalanceFreq, showLongerHistory),
});

const portfolioHoldingTypeToComparisonHoldingType = (holdingType: PortfolioHoldingType): ComparisonHoldingType => {
  if (holdingType === HOLDING_TYPES.CUSTOM_YIELD) {
    return HOLDING_TYPES.FUND;
  }

  return holdingType as ComparisonHoldingType;
};

export const portfolioHoldingToComparisonHolding = (holding: PortfolioHolding, data: Holding): Nullable<ComparisonHolding> => {
  if (!holding.original.id) {
    return null;
  }

  const comparisonHolding: ComparisonHolding = {
    id: holding.original.id,
    type: portfolioHoldingTypeToComparisonHoldingType(holding.original.type),
    name: holding.name,
    symbol: holding.symbol || undefined,
    weight: holding.weight,
    rolling: data.performance.rolling,
    baseRolling: data.currency === CURRENCY.USD ? data.performance.rollingUSD : data.performance.rolling,
  };

  if (holding.original.type === HOLDING_TYPES.BENCHMARK) {
    return {
      ...comparisonHolding,
      riskFreeId: (data as ComparisonBenchmark).riskFreeId,
    };
  }

  return comparisonHolding;
};

export const portfolioHoldingsToComparisonHoldings = (holdings: PortfolioHolding[], data: Holding[]) =>
  holdings.reduce((acc, holding) => {
    const match = data.find(d => d._id === holding.original.id);
    if (match) {
      const newHolding = portfolioHoldingToComparisonHolding(holding, match);
      if (newHolding) {
        acc.push(newHolding);
      }
    }
    return acc;
  }, [] as ComparisonHolding[]);

const PAYMENT_FREQUENCIES = {
  MONTHLY: 'monthly',
  ANNUALLY: 'annually',
  AT_MATURITY: 'maturity',
  DAILY: 'daily',
  WEEKLY: 'weekly',
};

const TERM_UNITS = {
  MONTHS: 'months',
  YEARS: 'years',
};

const getPeriodicalRate = (config: CustomYieldParams, maturityDate: Date) => {
  let denominator = 1;
  const { frequency, term, termUnits, rate, startDate } = config;
  if (frequency === PAYMENT_FREQUENCIES.MONTHLY && termUnits === TERM_UNITS.YEARS) {
    denominator = 12 * term;
  } else if (frequency === PAYMENT_FREQUENCIES.MONTHLY && termUnits === TERM_UNITS.MONTHS) {
    denominator = term;
  } else if (frequency === PAYMENT_FREQUENCIES.ANNUALLY && termUnits === TERM_UNITS.YEARS) {
    denominator = term;
  } else if (frequency === PAYMENT_FREQUENCIES.DAILY && termUnits === TERM_UNITS.YEARS) {
    denominator = term * 365;
  } else if (frequency === PAYMENT_FREQUENCIES.DAILY && termUnits === TERM_UNITS.MONTHS) {
    const days = daysBetween(startDate, maturityDate);
    denominator = days;
  } else if (frequency === PAYMENT_FREQUENCIES.WEEKLY && termUnits === TERM_UNITS.MONTHS) {
    denominator = (52 / 12) * term;
  } else if (frequency === PAYMENT_FREQUENCIES.WEEKLY && termUnits === TERM_UNITS.YEARS) {
    denominator = (52 / 12) * (term * 12);
  }
  const periodicalRate = (1 + rate / 100) ** (1 / denominator) - 1;
  return periodicalRate * 100;
};

const shouldPayInterest = (config: CustomYieldParams, maturityDate: Date, currentDate: Date) => {
  const { automaticRenewal, termUnits, frequency, startDate, term } = config;
  const isSameOrAfterMaturity = currentDate.getTime() >= maturityDate.getTime();
  const isSameOrBeforeMaturity = currentDate.getTime() <= maturityDate.getTime();
  const daysAfterStart = daysBetween(startDate, currentDate);
  const daysToMaturity = daysBetween(currentDate, maturityDate);
  if (frequency === PAYMENT_FREQUENCIES.AT_MATURITY) {
    if (automaticRenewal) {
      if (termUnits === TERM_UNITS.YEARS) {
        if (isSameOrAfterMaturity) {
          const years = Math.abs(yearsDiff(currentDate, maturityDate));
          if (years % term === 0) {
            const nextMaturity = moment.utc(maturityDate).add(years, 'years').startOf('day').toDate();
            return currentDate.getTime() === nextMaturity.getTime();
          }
          return false;
        }
      } else if (TERM_UNITS.MONTHS) {
        if (isSameOrAfterMaturity) {
          const months = Math.abs(monthsDiff(currentDate, maturityDate));
          if (months % term === 0) {
            const nextMaturity = moment.utc(maturityDate).add(months, 'months').startOf('day').toDate();
            return currentDate.getTime() === nextMaturity.getTime();
          }
          return false;
        }
      }
    }
    return daysToMaturity === 0;
  }
  if (frequency === PAYMENT_FREQUENCIES.DAILY) {
    if (automaticRenewal && daysAfterStart > 0) {
      return true;
    }
    return daysAfterStart > 0 && daysToMaturity >= 0;
  }
  if (frequency === PAYMENT_FREQUENCIES.MONTHLY) {
    if (automaticRenewal && daysAfterStart > 0) {
      const paymentDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 2));
      return paymentDate.getTime() === currentDate.getTime();
    }
    return daysAfterStart > 0 && isSameOrBeforeMaturity && daysAfterStart % 30 === 0;
  }
  if (frequency === PAYMENT_FREQUENCIES.ANNUALLY) {
    if (automaticRenewal && daysAfterStart > 0) {
      return daysAfterStart % 365 === 0;
    }
    return daysToMaturity === 0;
  }
  if (frequency === PAYMENT_FREQUENCIES.WEEKLY) {
    const timestamp = moment.utc(currentDate).startOf('day').weekday(5).valueOf();
    if (automaticRenewal) {
      return currentDate.getTime() === timestamp;
    }
    return isSameOrBeforeMaturity && currentDate.getTime() === timestamp;
  }
  return false;
};

type CustomReturnResponse = {
  performance: InstrumentPerformance;
  nav: {
    weekly: [Timestamp, number][];
    monthly: [Timestamp, number][];
  };
  distribution: [Timestamp, number][];
  dates: FundDates;
};

const createCustomYieldPerformanceFromConfig = (config: CustomYieldParams, monthEndDataOption?: PERIOD_END_DATA_OPTIONS): CustomReturnResponse => {
  const { termUnits, term, startDate } = config;
  let maturityDate = new Date();
  if (termUnits === TERM_UNITS.YEARS) {
    maturityDate = moment(startDate).utc().add(term, 'years').startOf('day').toDate();
  }
  if (termUnits === TERM_UNITS.MONTHS) {
    maturityDate = moment(startDate).utc().add(term, 'months').startOf('day').toDate();
  }
  const defaultStartDate = moment().utc().startOf('year').startOf('day').subtract('10', 'years').startOf('day').toDate();

  const endMomentDate = moment().utc();

  switch (monthEndDataOption) {
    case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
      endMomentDate.subtract(1, 'months').endOf('month').startOf('day');
      break;
    case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
      endMomentDate.subtract(2, 'months').endOf('month').startOf('day');
      break;
    case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
      endMomentDate.subtract(1, 'quarters').endOf('quarter').startOf('day');
      break;
    case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
      endMomentDate.subtract(2, 'quarters').endOf('quarter').startOf('day');
      break;
    default:
      endMomentDate.subtract(1, 'days').startOf('day');
      break;
  }

  const endDate = endMomentDate.toDate();
  const earlyStart = new Date(startDate).getTime() > defaultStartDate.getTime();
  const days = earlyStart ? daysBetween(defaultStartDate, endDate) : daysBetween(startDate, endDate);
  const effectiveStart = earlyStart ? defaultStartDate : startDate;

  const prices: PriceData[] = [];
  const navs: PriceData[] = [];
  const calculationData = [];
  const distribution: [Timestamp, number][] = [];
  const rate = getPeriodicalRate(config, maturityDate);

  for (let i = 0; i <= days; i++) {
    const currentDate = moment(effectiveStart).utc().startOf('day').add(i, 'd');
    const payInterest = shouldPayInterest(config, maturityDate, currentDate.toDate());
    const nav = 1;

    let price: number = i === 0 ? 1 : calculationData[i - 1].price;
    if (payInterest) {
      if (calculationData[i - 1] != null) {
        price += calculationData[i - 1].price * (rate / 100);
      } else {
        price += 1 * (rate / 100);
      }
      distribution.push([currentDate.toDate().getTime(), nav * (rate / 100)]);
    }

    const rolling = payInterest ? rate : 0;
    calculationData.push({ date: currentDate.toDate(), payInterest, price, rolling });

    prices.unshift({ price, date: currentDate.toDate() });
    navs.unshift({ price: nav, date: currentDate.toDate() });
  }
  const monthlyRolling = calculateMonthlyRollings(prices);
  const weeklyRolling = calculateWeeklyRollings(prices);
  const weeklyNAV = calculateWeeklyNAV(navs);
  const monthlyNAV = calculateMonthlyNAV(navs);
  const percentGrowth = calculatePercentGrowthFromPrices(prices, true);

  const defaultTime = Date.now();
  const lastTimestamp = monthlyRolling.length > 0 ? monthlyRolling[monthlyRolling.length - 1][0] : defaultTime;
  const firstTimestamp = monthlyRolling.length > 0 ? monthlyRolling[0][0] : defaultTime;
  const validPeriods = getValidPeriods(firstTimestamp, lastTimestamp);
  const riskDate = new Date(lastTimestamp);
  const growthDate = prices.length > 0 ? new Date(prices[0].date) : undefined;

  const risk = initRiskData();
  for (const validPeriod of validPeriods) {
    const rawRolling = formatRollings(monthlyRolling, validPeriod.sliceCount, firstTimestamp, lastTimestamp);
    const { standardDeviation } = getVarianceMetrics(rawRolling);
    const maxDrawdown = calculateMaximumDrawdown(rawRolling);
    risk.standardDeviation = { ...risk.standardDeviation, [validPeriod.period]: standardDeviation != null ? standardDeviation * Math.sqrt(12) : null };
    risk.maximumDrawdown = { ...risk.maximumDrawdown, [validPeriod.period]: maxDrawdown };
  }

  const bestWorst = calculateBestWorstPerformance(monthlyRolling);
  const upDownMonths = calculateUpDownMonths(monthlyRolling.map(r => r[1]));
  const calendarYear = calculateCalendarYearFromRolling(monthlyRolling);

  return {
    performance: {
      rolling: {
        monthly: monthlyRolling,
        weekly: weeklyRolling,
      },
      rollingUSD: {
        monthly: [],
        weekly: [],
      },
      ...risk,
      bestWorst,
      upDownMonths,
      percentGrowthUSD: { ...DEFAULT_GROWTH_VALUES },
      percentGrowth,
      calendarYear,
    },
    nav: {
      monthly: monthlyNAV,
      weekly: weeklyNAV,
    },
    distribution,
    dates: {
      inception: startDate,
      growth: growthDate,
      risk: {
        endDate: riskDate,
      },
    },
  } as unknown as CustomReturnResponse;
};

export const generateFundFromCustomYieldConfig = (
  params: CustomYieldParams,
  shelfInfo: ShelvableInstrument & CompliantInstrument,
  currency: string | undefined,
  id?: string,
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS,
): Fund => {
  const { name, benchmarkLipperId } = params;
  const { performance, nav, dates, distribution } = createCustomYieldPerformanceFromConfig(params, monthEndDataOption);
  const allocations: Allocations = {
    asset: [
      {
        name: {
          [LOCALE.EN_CA]: 'Cash',
          [LOCALE.FR_CA]: 'Liquidités',
        },
        weight: 100,
      },
    ],
    country: [
      {
        name: currency === CURRENCY.USD ? 'US' : 'CAN',
        weight: 100,
      },
    ],
  };

  return {
    _id: id || generateBSONId(),
    lipperId: 0,
    currency: currency || CURRENCY.CAD,
    fundPortfolio: {
      allocations,
      holdings: { list: [{ name, weight: 100 }] },
    },
    rrspEligible: true,
    distribution,
    assetGroup: ASSET_GROUPS.CASH,
    performance,
    benchmarkLipperId,
    riskFreeLipperId: benchmarkLipperId,
    filteredShelves: shelfInfo.filteredShelves,
    complianceComparisons: shelfInfo.complianceComparisons,
    dates,
    nav,
    assetType: {
      [LOCALE.EN_CA]: 'Cash',
      [LOCALE.FR_CA]: 'Liquidités',
    },
    classification: {
      [LOCALE.EN_CA]: 'Money Market',
      [LOCALE.FR_CA]: 'Liquidités',
    },
    names: {
      full: {
        [LOCALE.EN_CA]: name,
        [LOCALE.FR_CA]: name,
      },
      short: {
        [LOCALE.EN_CA]: name,
        [LOCALE.FR_CA]: name,
      },
    },
    riskRating: {
      [LOCALE.EN_CA]: 'Very Low',
      [LOCALE.FR_CA]: 'Très Faible',
    },
    universe: {
      [LOCALE.EN_CA]: 'Savings Accounts',
      [LOCALE.FR_CA]: "Comptes d'épargne",
    },
    customYield: params,
    symbols: [formatMessage(`Common.${params.type}`)],
  };
};

/**
 * Create generic clone of a portfolio with temp id and "Copy of" prefix, unless custom prefix id specified.
 *
 * @param portfolio Portfolio.
 * @returns cloned portfolio.
 */
export const createPortfolioClone = (portfolio: Portfolio, customPrefixId?: string): Portfolio => {
  const portfolioClone = deepClone(portfolio);
  portfolioClone._id = generateTempId();
  portfolioClone.correlationId = portfolioClone._id.replace('new-', '');
  const enCaName = portfolioClone.name[LOCALE.EN_CA] || '';
  const frCaName = portfolioClone.name[LOCALE.FR_CA] || '';
  const enUsName = portfolioClone.name[LOCALE.EN_US] || '';
  portfolioClone.name = {
    [LOCALE.EN_CA]: generateItemCopyName(enCaName, customPrefixId || 'Portfolios.CopyPrefix'),
    [LOCALE.FR_CA]: generateItemCopyName(frCaName, customPrefixId || 'Portfolios.CopyPrefix'),
    ...(enUsName ? { [LOCALE.EN_US]: generateItemCopyName(enUsName, customPrefixId || 'Portfolios.CopyPrefix') } : {}),
  };

  if (portfolioClone.riskProfile) {
    delete portfolioClone.riskProfile._id;
  }

  portfolioClone.isCloned = true;

  return portfolioClone;
};

/**
 * Clone portfolio with proxy prefix. Set allocation mode to units by default.
 *
 * @param portfolio Portfolio.
 * @returns clone of portfolio as proxy.
 */
export const clonePortfolioAsProxy = (portfolio: Portfolio): Portfolio => createPortfolioClone(portfolio, 'Portfolios.ProxyCopyPrefix');

/**
 * Get missing holdings from portfolio.
 *
 * @param portfolio Portfolio.
 * @returns List of holdings
 */
export const getPortfolioMissingHoldings = (portfolio: Portfolio): PortfolioHolding[] => portfolio.holdings.filter(h => disclaimers.holdingIsMissing(h));

/**
 * Gets the holdings from a portfolio that have missing data OR invalid financial values.
 * Returns the actual holdings that have issues rather than just a boolean.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns Array of holdings that have missing data or invalid financial values (zero/null/negative units, weight, or dollars).
 */
export const getPortfolioMissingOrInvalidHoldings = (portfolio: Portfolio): PortfolioHolding[] =>
  portfolio.holdings.filter(disclaimers.holdingIsMissingOrInvalid);

/**
 * Get disclaimers for a portfolio.
 *
 * @param portfolio Portfolio.
 * @returns disclaimer, or undefined in none found.
 */

export const getPortfolioDisclaimer = (portfolio: Portfolio) => {
  // Disclaimer keys displayed on the holding, in order of importance.
  const DISPLAYED_DISCLAIMERS = [DISCLAIMER_KEYS.PORTFOLIO_MISSING_HOLDINGS];

  // Find first key that matches an active disclaimer on the holding.
  const holdingDisclaimers = disclaimers.getPortfolioDisclaimers(portfolio);
  const disclaimerKey = DISPLAYED_DISCLAIMERS.find(key => !!holdingDisclaimers[key]);

  if (disclaimerKey) {
    return holdingDisclaimers[disclaimerKey];
  }

  return undefined;
};

const mergePartiallyOverridenRollings = (forcedRollings: [Timestamp, number][], currentRollings: [Timestamp, number][]): [Timestamp, number][] => {
  const endPoint = forcedRollings[forcedRollings.length - 1][0];
  const mergedRollings = [
    ...forcedRollings,
    // trim history older than (or equal to) the end of the data override.
    ...currentRollings.filter(data => data[0] > endPoint),
  ];
  return mergedRollings;
};

/**
 * Gets the portfolio rolling data taking into account `override` and `isPartiallyOverriden`.
 * Holdings are forced to rebalance Monthly.
 */
export const getRollingDataForInterval = (
  portfolio: Portfolio,
  holdings: ComparisonHolding[],
  interval: Interval,
  fees: number,
  shortHistory?: boolean,
): { rolling: [Timestamp, number][]; override: boolean } => {
  let rolling: [Timestamp, number][] = [];

  const forcedRolling = portfolio.override?.rolling != null ? (portfolio.override.rolling as RollingPerformance)[interval] : [];
  const override = forcedRolling.length > 0;

  if (override) {
    rolling = portfolio.isPartiallyOverriden
      ? mergePartiallyOverridenRollings(forcedRolling, rollingPerformance(holdings, interval, fees, undefined, shortHistory))
      : forcedRolling;
  } else {
    rolling = rollingPerformance(holdings, interval, fees, undefined, shortHistory);
  }

  return { rolling, override };
};

export const getRollingData = (
  portfolio: Portfolio,
  holdings: ComparisonHolding[],
  fees: number,
  shortHistory?: boolean,
): { rolling: RollingPerformance; override: boolean } => {
  const monthly = getRollingDataForInterval(portfolio, holdings, Interval.Monthly, fees, shortHistory);
  const weekly = getRollingDataForInterval(portfolio, holdings, Interval.Weekly, fees, shortHistory);
  return { rolling: { monthly: monthly.rolling, weekly: weekly.rolling }, override: monthly.override };
};

export const isEnterpriseModel = (portfolio?: Nullable<Partial<Portfolio>>): boolean => {
  if (portfolio == null) {
    return false;
  }

  return portfolio.enterprise != null || (portfolio.enterpriseGroups != null && portfolio.enterpriseGroups.length > 0);
};

export const loadPortfoliosIfHoldingsAreMissing = async (
  portfolioList: Portfolio[],
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>,
) => {
  // the API now supports receiving empty holding arrays (when we get a list)
  const promises: Array<Promise<Portfolio>> = portfolioList.map(
    portfolio =>
      new Promise(async (resolve, reject) => {
        try {
          if (portfolio.holdings.length > 0) {
            resolve(portfolio);
            return;
          }
          const newPortfolio = await getPortfolioById(
            portfolio._id!,
            getPortfolioType(portfolio),
            portfolio.contact != null ? portfolio.contact._id : undefined,
            portfolio.enterprise,
            undefined,
            portfolio,
          );
          resolve(newPortfolio);
        } catch (error) {
          reject(error);
        }
      }),
  );
  const portfolios = await Promise.all(promises);
  return portfolios;
};

export const loadPortfolioIfHoldingsAreMissing = async (
  portfolio: Portfolio,
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>,
) => {
  const portfolios = await loadPortfoliosIfHoldingsAreMissing([portfolio], getPortfolioById);
  return portfolios[0];
};
